import React, {useEffect, useState} from 'react'
import {BoxProps, Button, Flex, Text} from '../../vanilla'
import {useSlotExpiration} from '../../store/hooks/useSlotExpiration'
import {useLocation} from 'react-router-dom'
import {redirectIfNotRegistered} from '../../utils/login-redirect-utils'
import {
  useBasketStore,
  useCustomerStore,
  useOrderStore,
  useProductStore
} from '../../store/hooks/useStore'
import useNavigation from '../../hooks/use-navigation'
import classNames from 'classnames'
import {MinusIcon, PlusIcon} from '../../components/icons'
import {
  quantityInputButton,
  quantityInputRecipeButton,
  quantityInputRecipeInput
} from '../../components/quantity-input/style.css'
import {MealDealGroup} from '../../types/store/basket'
import {flowResult} from 'mobx'
import {getMealDealQuantityUpdates, isMealDealProductMaxReached} from './meal-deal-builder-helper'
import type {PartialProductItem} from '../../types/store/basket'
import {useAddToCartTracking, useRemoveFromCartTracking} from '../../analytics/click-event-tracking'
import {usePromotion} from '../../store/hooks/usePromotion'

export interface MealDealQuantityInputProps extends Omit<BoxProps<'div'>, 'onChange' | 'value'> {
  size?: 'md' | 'sm'
  variant?: 'primary' | 'outlineDark'
  disabled?: boolean
  cartLocation?: string
  inCart?: boolean
  group: MealDealGroup | null
}

const MealDealQuantityInput = ({
  size = 'md',
  disabled,
  variant = 'primary',
  cartLocation,
  inCart = false,
  group,
  ...rest
}: MealDealQuantityInputProps) => {
  const [isMaxLimitReached, setIsMaxLimitReached] = useState(false)
  const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()
  const basketStore = useBasketStore()
  const {productsById} = useProductStore()

  const customerStore = useCustomerStore()
  const {isRegistered, loading: loadingCustomer} = customerStore
  const {editMode} = useOrderStore()

  const navigate = useNavigation()
  const {pathname} = useLocation()

  const sendRemoveFromCartData = useRemoveFromCartTracking()
  const sendAddToCartData = useAddToCartTracking(basketStore.basket)
  const promotion = usePromotion(group?.promoID)

  const quantity = Number(group?.quantity ?? 0)
  const maxQuantity = Number(group?.promo?.maxApp ?? 0)
  const mealDealGroups = (basketStore.basket?.mealdeals?.groupedItems || []) as MealDealGroup[]
  const totalPromoQuantity = mealDealGroups
    .filter((item) => item.promoID === group?.promoID)
    .reduce((total, item) => total + item.quantity, 0)

  useEffect(() => {
    if (!group) return
    if (
      (maxQuantity > 0 && totalPromoQuantity >= maxQuantity) ||
      isMealDealProductMaxReached(group, basketStore.basket?.productItems || [], productsById)
    ) {
      setIsMaxLimitReached(true)
    } else {
      setIsMaxLimitReached(false)
    }
  }, [quantity, maxQuantity, basketStore.basket?.productItems])

  /**
   * Creates the cart event data for the given products to update
   * @returns The cart event data object containing everything needed for analytics tracking 
   */
  const createCartEventData = () => {
    return {
      product:
        group?.items
          .map((item) => {
            
            const product = productsById[item.productID]

            // Use the display quantity on the product; should represent item quantity within a single meal deal. 
            if (product) {
              product.quantity = item.displayQuantity
            } 

            return product
          })
          .filter((product) => product != null) || [],
      cartLocation,
      value: promotion?.price,
      mealDealId: promotion?.id
    }
  }

  const updateQuantity = async (e: React.MouseEvent, isIncrease: boolean) => {
    e.preventDefault()
    e.stopPropagation()

    if (!group) {
      return
    }

    if (editMode && isReservationExpired()) {
      handleEditReservationExpired()
      return
    }

    customerStore.setLastActiveToNow()

    const newQuantity = isIncrease ? quantity + 1 : quantity - 1

    if (newQuantity < 0) {
      return
    }

    if (isIncrease && maxQuantity > 0 && (newQuantity > maxQuantity || isMaxLimitReached)) {
      return
    }

    const updatedProducts = getMealDealQuantityUpdates(
      group,
      newQuantity,
      basketStore.basket?.productItems || []
    )

    if (!updatedProducts.length) {
      return
    }

    const productsToUpdate: PartialProductItem[] = updatedProducts.map((item) => ({
      product: {
        id: item.productId
      } as PartialProductItem['product'],
      quantity: item.quantity
    }))

    try {
      const promoId = isIncrease ? group.promoID : undefined
      const response = await flowResult(
        basketStore.updateBasketItemQuantity(productsToUpdate, {
          openMiniBasket: false,
          processAsGroup: true,
          promoId: promoId
        })
      )

      if (response?.success !== true || response?.failed?.length) {
        return
      }

      // Do the analytics tracking for adding/removing items from the cart only if the update was successful
      const cartEventData = createCartEventData()

      if (isIncrease) {
        sendAddToCartData(cartEventData)
      } else {
        sendRemoveFromCartData(cartEventData)
      }

    } catch (error) {
      console.error('Failed to update meal deal quantity', error)
    }
  }

  const buttons = quantityInputRecipeButton({
    size,
    variant
  })
  const input = quantityInputRecipeInput({size, variant})

  return (
    <Flex alignItems="center" flexDirection="column" {...rest}>
      <Flex>
        <Button
          data-test-selector="decrease-quantity-button"
          extraClassName={classNames(quantityInputButton, buttons)}
          aria-label="Decrease quantity"
          flexShrink="0"
          variant={variant}
          onClick={(e) => {
            if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer})) {
              updateQuantity(e, false)
            }
          }}
          disabled={disabled}
          size="sm"
          iconLeft={
            <MinusIcon
              style={{
                height: size === 'sm' ? 10 : 12,
                width: size === 'sm' ? 10 : 12,
                pointerEvents: 'none'
              }}
            />
          }
        />
        <Text
          variant="unstyled"
          className={input}
          flexShrink="1"
          alignSelf="stretch"
          data-test-selector={inCart ? 'cart-meal-quantity-value' : 'builder-meal-quantity-value'}
          style={variant === 'outlineDark' ? {} : {color: '#0097cc'}}
        >
          {isMaxLimitReached ? 'Max Qty' : `${group?.quantity ? group?.quantity : 0}`}
        </Text>

        <Button
          data-test-selector="increase-quantity-button"
          variant={variant}
          extraClassName={classNames(quantityInputButton, buttons)}
          aria-label="Increase quantity"
          flexShrink="0"
          onClick={(e) => {
            if (isMaxLimitReached) {
              return
            }
            if (!redirectIfNotRegistered({navigate, pathname, isRegistered, loadingCustomer})) {
              updateQuantity(e, true)
            }
          }}
          disabled={disabled}
          style={{
            opacity: isMaxLimitReached ? 0.3 : disabled ? 0.65 : 1,
            cursor: isMaxLimitReached ? 'not-allowed' : 'pointer'
          }}
          size="sm"
          iconLeft={
            <PlusIcon
              style={{
                height: size === 'sm' ? 10 : 12,
                width: size === 'sm' ? 10 : 12,
                pointerEvents: 'none'
              }}
            />
          }
        />
      </Flex>
      {cartLocation !== 'Quick Search' && isMaxLimitReached && (
        <Text color="accent0"> Maximum reached</Text>
      )}
    </Flex>
  )
}

export default MealDealQuantityInput
